"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Great_Vibes, Cormorant_Garamond } from "next/font/google";
import {
  getCameraMicrophoneErrorMessage,
  requestCameraMicrophoneStream,
} from "@/app/lib/cameraAccess";

const greatVibes = Great_Vibes({
  subsets: ["latin"],
  weight: "400",
});

const cormorant = Cormorant_Garamond({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
});

type MediaItem = {
  id: string;
  url: string;
  type: "image" | "video";
  title: string;
};

const CONFETTI_STORAGE_KEY = "weddingHomepageConfettiEnabled";
const CAMERA_MIC_PERMISSION_STORAGE_KEY = "weddingCameraMicrophoneAccessReady";

type CameraMicrophonePermissionState =
  | "granted"
  | "prompt"
  | "denied"
  | "unknown";

async function getCameraMicrophonePermissionState(): Promise<CameraMicrophonePermissionState> {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) {
    return "unknown";
  }

  try {
    const [cameraPermission, microphonePermission] = await Promise.all([
      navigator.permissions.query({ name: "camera" } as PermissionDescriptor),
      navigator.permissions.query({ name: "microphone" } as PermissionDescriptor),
    ]);

    if (cameraPermission.state === "granted" && microphonePermission.state === "granted") {
      return "granted";
    }

    if (cameraPermission.state === "denied" || microphonePermission.state === "denied") {
      return "denied";
    }

    return "prompt";
  } catch {
    return "unknown";
  }
}

function getStoredCameraMicrophoneAccess() {
  try {
    return window.localStorage.getItem(CAMERA_MIC_PERMISSION_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function setStoredCameraMicrophoneAccess(isReady: boolean) {
  try {
    if (isReady) {
      window.localStorage.setItem(CAMERA_MIC_PERMISSION_STORAGE_KEY, "1");
    } else {
      window.localStorage.removeItem(CAMERA_MIC_PERMISSION_STORAGE_KEY);
    }
  } catch {}
}

function ConfettiOverlay() {
  const pieces = Array.from({ length: 12 }, (_, i) => i);

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-35">
      <style jsx>{`
        @keyframes confettiFall {
          0% {
            transform: translateY(-12vh) rotate(0deg);
            opacity: 0;
          }
          12% {
            opacity: 0.65;
          }
          100% {
            transform: translateY(110vh) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes confettiDrift {
          0% {
            margin-left: 0px;
          }
          25% {
            margin-left: 6px;
          }
          50% {
            margin-left: -8px;
          }
          75% {
            margin-left: 5px;
          }
          100% {
            margin-left: 0px;
          }
        }
      `}</style>

      {pieces.map((piece) => {
        const left = `${(piece * 8.2) % 100}%`;
        const delay = `${(piece % 6) * 1.1}s`;
        const duration = `${10 + (piece % 4)}s`;
        const width = `${6 + (piece % 3)}px`;
        const height = `${10 + (piece % 4)}px`;
        const background =
          piece % 3 === 0
            ? "rgba(31,78,121,0.22)"
            : piece % 3 === 1
              ? "rgba(212,175,55,0.20)"
              : "rgba(148,163,184,0.22)";

        return (
          <span
            key={piece}
            className="absolute top-0 block rounded-full"
            style={{
              left,
              width,
              height,
              background,
              animation: `confettiFall ${duration} linear ${delay} infinite, confettiDrift 4.2s ease-in-out ${delay} infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

function RoseCorner({ className = "" }: { className?: string }) {
  return (
    <div className={`pointer-events-none absolute ${className}`}>
      <div className="relative h-44 w-44 sm:h-52 sm:w-52">
        <div className="absolute left-5 top-5 h-30 w-30 border border-[#caa23a]" />
        <div className="absolute left-9 top-9 h-30 w-30 border border-[#d9b24d]/70" />

        <div className="absolute left-0 top-3 h-16 w-16 rounded-full bg-[#1f4e79] shadow-[inset_0_0_0_5px_rgba(255,255,255,0.08)]">
          <div className="absolute inset-[22%] rounded-full border-2 border-[#183d61]/80" />
          <div className="absolute inset-[36%] rounded-full border-2 border-[#183d61]/70" />
        </div>

        <div className="absolute left-10 top-0 h-14 w-14 rounded-full bg-[#2f67aa] shadow-[inset_0_0_0_5px_rgba(255,255,255,0.08)]">
          <div className="absolute inset-[24%] rounded-full border-2 border-[#28588e]/80" />
          <div className="absolute inset-[38%] rounded-full border-2 border-[#28588e]/70" />
        </div>

        <div className="absolute left-17 top-9 h-13 w-13 rounded-full bg-[#173d61] shadow-[inset_0_0_0_5px_rgba(255,255,255,0.08)]">
          <div className="absolute inset-[24%] rounded-full border-2 border-[#102c47]/80" />
          <div className="absolute inset-[38%] rounded-full border-2 border-[#102c47]/70" />
        </div>

        <div className="absolute left-7 top-18 h-13 w-13 rounded-full bg-[#3f78ba] shadow-[inset_0_0_0_5px_rgba(255,255,255,0.08)]">
          <div className="absolute inset-[24%] rounded-full border-2 border-[#2f67aa]/80" />
          <div className="absolute inset-[38%] rounded-full border-2 border-[#2f67aa]/70" />
        </div>

        <div className="absolute left-22 top-20 h-15 w-15 rounded-full bg-[#1f4e79] shadow-[inset_0_0_0_5px_rgba(255,255,255,0.08)]">
          <div className="absolute inset-[22%] rounded-full border-2 border-[#183d61]/80" />
          <div className="absolute inset-[36%] rounded-full border-2 border-[#183d61]/70" />
        </div>

        <div className="absolute left-28 top-3 h-7 w-11 rotate-12 rounded-full bg-[#94a3b8]/75" />
        <div className="absolute left-33 top-16 h-7 w-11 -rotate-12 rounded-full bg-[#94a3b8]/65" />
        <div className="absolute left-1 top-31 h-7 w-11 rotate-45 rounded-full bg-[#94a3b8]/65" />
        <div className="absolute left-31 top-31 h-7 w-11 -rotate-45 rounded-full bg-[#94a3b8]/75" />
      </div>
    </div>
  );
}

function GoldDivider() {
  return (
    <div className="flex items-center justify-center gap-4 py-2">
      <span className="h-px w-16 bg-[#caa23a]" />
      <span className="text-lg text-[#caa23a]">✦</span>
      <span className="h-px w-16 bg-[#caa23a]" />
    </div>
  );
}

function PreviewScroller({ items }: { items: MediaItem[] }) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {items.map((item) => (
        <div
          key={item.id}
          className="min-w-35 overflow-hidden rounded-2xl border border-[#e4ddd0] bg-white shadow-sm"
        >
          <div className="relative h-28 w-35 bg-[#ebe7df]">
            {item.type === "image" ? (
              <Image src={item.url} alt={item.title} fill className="object-cover" />
            ) : (
              <video
                src={item.url}
                className="h-full w-full object-cover"
                muted
                playsInline
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Page() {
  const uploadInputId = useId();
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [cameraStatus, setCameraStatus] = useState("");
  const [isCameraChecking, setIsCameraChecking] = useState(false);
  const [showAccessPrompt, setShowAccessPrompt] = useState(false);
  const [isAccessPromptOpen, setIsAccessPromptOpen] = useState(false);
  const [confettiEnabled, setConfettiEnabled] = useState(true);

  const [previewItems] = useState<MediaItem[]>([
    {
      id: "img-1",
      url: "/johnmax.jpg",
      type: "image",
      title: "Preview 1",
    },
    {
      id: "img-2",
      url: "/johnmax.jpg",
      type: "image",
      title: "Preview 2",
    },
    {
      id: "vid-1",
      url: "/sample-video.mp4",
      type: "video",
      title: "Preview video",
    },
  ]);

  const photoItems = useMemo(
    () => previewItems.filter((item) => item.type === "image"),
    [previewItems]
  );
  const videoItems = useMemo(
    () => previewItems.filter((item) => item.type === "video"),
    [previewItems]
  );

  useEffect(() => {
    try {
      setConfettiEnabled(window.localStorage.getItem(CONFETTI_STORAGE_KEY) !== "0");
    } catch {}
  }, []);

  useEffect(() => {
    let isMounted = true;

    void (async () => {
      const permissionState = await getCameraMicrophonePermissionState();
      if (!isMounted) return;

      if (permissionState === "granted") {
        setStoredCameraMicrophoneAccess(true);
        setShowAccessPrompt(false);
        return;
      }

      if (permissionState === "denied" || permissionState === "prompt") {
        setStoredCameraMicrophoneAccess(false);
        setShowAccessPrompt(true);
        return;
      }

      setShowAccessPrompt(!getStoredCameraMicrophoneAccess());
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  function openUploadPicker() {
    const input = uploadInputRef.current;
    if (!input) {
      setUploadStatus("File picker could not open. Try opening this page in Safari if Chrome has issues.");
      return;
    }

    try {
      input.value = "";
      input.click();
    } catch (error) {
      console.error(error);
      setUploadStatus("File picker could not open. Try opening this page in Safari if Chrome has issues.");
    }
  }

  function handleUploadKeyDown(event: React.KeyboardEvent<HTMLLabelElement>) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openUploadPicker();
  }

  async function enableCameraMicrophone() {
    if (isCameraChecking) return;

    try {
      setIsCameraChecking(true);
      setCameraStatus("");
      const stream = await requestCameraMicrophoneStream();
      stream.getTracks().forEach((track) => track.stop());
      setStoredCameraMicrophoneAccess(true);
      setShowAccessPrompt(false);
      setCameraStatus("");
    } catch (error) {
      console.error(error);
      setStoredCameraMicrophoneAccess(false);
      setShowAccessPrompt(true);
      setIsAccessPromptOpen(true);
      setCameraStatus(getCameraMicrophoneErrorMessage(error));
    } finally {
      setIsCameraChecking(false);
    }
  }

  function dismissAccessPrompt() {
    setIsAccessPromptOpen(false);
    setShowAccessPrompt(false);
  }

  function toggleConfetti() {
    setConfettiEnabled((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(CONFETTI_STORAGE_KEY, next ? "1" : "0");
      } catch {}
      return next;
    });
  }

  function handleFiles(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const selectedFiles = Array.from(files);

    void (async () => {
      try {
        setUploadStatus("Uploading your memories...");
        const formData = new FormData();
        selectedFiles.forEach((file) => formData.append("files", file));

        const response = await fetch("/api/media", {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          throw new Error("Upload failed");
        }

        const uploadedCount = selectedFiles.length;
        setUploadStatus(
          uploadedCount === 1
            ? "1 file uploaded successfully."
            : `${uploadedCount} files uploaded successfully.`
        );
      } catch (error) {
        console.error(error);
        setUploadStatus("Upload failed. Please try again. Try opening this page in Safari if Chrome has issues.");
      } finally {
        event.target.value = "";
      }
    })();
  }

  return (
    <main className={`min-h-screen bg-[#f6f4ef] text-slate-800 ${cormorant.className}`}>
      {showAccessPrompt ? (
        <div className="sticky top-0 z-50 border-b border-[#d6b552] bg-[#fff3bd]/95 text-[#1f3f6d] shadow-[0_10px_28px_rgba(31,78,121,0.10)] backdrop-blur motion-safe:animate-[permissionDropIn_320ms_ease-out_both]">
          <div className="mx-auto flex min-h-11 max-w-7xl items-center gap-2 px-3 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => setIsAccessPromptOpen((current) => !current)}
              aria-expanded={isAccessPromptOpen}
              className="flex min-w-0 flex-1 cursor-pointer items-center justify-center gap-2 py-2 text-center text-[11px] font-semibold tracking-[0.04em] sm:text-sm"
            >
              <span className="relative inline-flex h-2 w-2 shrink-0 rounded-full bg-[#1f4e79]">
                <span className="absolute inset-0 rounded-full bg-[#1f4e79] opacity-35 motion-safe:animate-ping" />
              </span>
              <span className="truncate">
                Camera &amp; microphone access
                <span className="hidden sm:inline">
                  {" "}
                  — enable once for guest videos and photo booth memories
                </span>
              </span>
              <span className="hidden font-bold text-[#8c6a18] sm:inline">
                Set up now
              </span>
            </button>

            <button
              type="button"
              onClick={() => setIsAccessPromptOpen((current) => !current)}
              aria-label={isAccessPromptOpen ? "Collapse permission banner" : "Expand permission banner"}
              className="inline-flex min-h-8 shrink-0 items-center justify-center rounded-full bg-white/55 px-3 text-[10px] font-bold uppercase tracking-[0.14em] text-[#1f4e79] transition hover:bg-white/80 sm:text-xs"
            >
              {isAccessPromptOpen ? "Hide" : "Open"}
            </button>

            <button
              type="button"
              onClick={dismissAccessPrompt}
              aria-label="Dismiss permission banner"
              className="inline-flex min-h-8 shrink-0 items-center justify-center rounded-full px-3 text-[10px] font-bold uppercase tracking-[0.14em] text-[#8c6a18] transition hover:bg-white/55 sm:text-xs"
            >
              Close
            </button>
          </div>

          <div
            className={`grid transition-[grid-template-rows,opacity,transform] duration-300 ease-out ${
              isAccessPromptOpen
                ? "grid-rows-[1fr] opacity-100 translate-y-0"
                : "grid-rows-[0fr] opacity-0 -translate-y-2"
            }`}
          >
            <div className="min-h-0 overflow-hidden">
              <div className="border-t border-[#e2c15d]/70 bg-[#fffaf0]/88">
                <div className="mx-auto grid max-w-7xl gap-3 px-4 py-4 sm:grid-cols-[1fr_auto] sm:items-center sm:px-6 lg:px-8">
                  <div>
                    <p className="text-sm font-semibold text-[#1f3f6d]">
                      Allow camera and microphone access when your browser asks.
                    </p>
                    <p className="mt-1 text-sm leading-6 text-[#686861]">
                      This keeps video uploads, framed photos, and photobooth tools ready for guests.
                    </p>
                    {cameraStatus ? (
                      <p className="mt-2 text-sm leading-6 text-[#1f4e79]" aria-live="polite">
                        {cameraStatus}
                      </p>
                    ) : null}
                  </div>

                  <button
                    type="button"
                    onClick={enableCameraMicrophone}
                    disabled={isCameraChecking}
                    className="inline-flex min-h-11 items-center justify-center rounded-full bg-[#1f4e79] px-6 py-3 text-center text-xs font-semibold uppercase tracking-[.14em] text-white transition hover:bg-[#173d61] disabled:opacity-50"
                  >
                    {isCameraChecking ? "Checking..." : "Allow Camera & Mic"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <section className="relative mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        {confettiEnabled ? <ConfettiOverlay /> : null}

        <div className="relative overflow-hidden rounded-[34px] border border-[#e7dfcf] bg-[#f9f7f2] shadow-[0_24px_80px_rgba(15,23,42,0.10)]">
          <RoseCorner className="left-2 top-2" />
          <RoseCorner className="right-2 top-2 scale-x-[-1]" />
          <RoseCorner className="bottom-2 left-2 scale-y-[-1]" />
          <RoseCorner className="bottom-2 right-2 scale-x-[-1] scale-y-[-1]" />

          <div className="relative z-10 px-5 py-10 sm:px-10 sm:py-14 lg:px-20">
            <div className="mx-auto max-w-4xl text-center">
              <p className="text-[11px] uppercase tracking-[0.34em] text-[#8c8c86]">
                Wedding Guest Gallery
              </p>

              <h1
                className={`${greatVibes.className} wedding-gold-halo mt-5 text-6xl leading-none text-[#1f3f6d] sm:text-7xl lg:text-8xl`}
              >
                Wedding
              </h1>

              <p className="mt-4 text-base text-[#7a7a72] sm:text-lg">
                You are invited to celebrate and share the memories of
              </p>

              <h2
                className={`${greatVibes.className} wedding-gold-halo mt-5 text-5xl leading-none text-[#1f3f6d] sm:text-6xl lg:text-7xl`}
              >
                John &amp; Maxine
              </h2>

              <div className="mt-5">
                <GoldDivider />
              </div>

              <div className="mt-7 grid gap-5 text-center sm:grid-cols-3">
	                <div className="rounded-3xl border border-[#ead8a0] bg-white/70 px-4 py-5 shadow-sm">
                  <p className="text-sm uppercase tracking-[0.18em] text-[#9a9a92]">
                    Date
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-[#1f3f6d] sm:text-4xl">
                    14 · 8 · 26
                  </p>
                </div>

	                <div className="rounded-3xl border border-[#ead8a0] bg-white/70 px-4 py-5 shadow-sm">
                  <p className="text-sm uppercase tracking-[0.18em] text-[#9a9a92]">
                    Time
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-[#1f3f6d] sm:text-4xl">
                    3:30 PM
                  </p>
                </div>

	                <div className="rounded-3xl border border-[#ead8a0] bg-white/70 px-4 py-5 shadow-sm">
                  <p className="text-sm uppercase tracking-[0.18em] text-[#9a9a92]">
                    Venue
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-[#1f3f6d] sm:text-4xl">
                    Oakwood House
                  </p>
                </div>
              </div>

              <div className="mx-auto mt-8 max-w-3xl rounded-[1.75rem] border border-[#decf9b] bg-[linear-gradient(180deg,#fffdfa_0%,#f5eee0_100%)] px-6 py-7 shadow-sm">
                <p className="text-[11px] uppercase tracking-[.28em] text-[#8c8c86]">
                  After Party
                </p>
                <h3 className="mt-3 text-3xl font-semibold text-[#1f3f6d] sm:text-4xl">
                  Tovil Working Mens Club
                </h3>
                <p className="mt-4 text-base leading-8 text-[#686861] sm:text-lg">
                  Join the celebration after the wedding at Tovil Hill, Maidstone
                  ME15 6QS. If you are saving one extra screenshot, make it this.
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
                  <Link
                    href="/after-party"
                    className="inline-flex min-h-14 items-center justify-center rounded-full bg-[#1f4e79] px-8 py-3 text-sm font-semibold uppercase tracking-[.14em] text-white transition hover:bg-[#173d61]"
                  >
                    After Party Details
                  </Link>
                  <a
                    href="https://maps.google.com/?q=Tovil+Working+Mens+Club+Tovil+Hill+Maidstone+ME15+6QS"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-14 items-center justify-center rounded-full border border-[#caa23a]/60 bg-[#f8f5ed] px-8 py-3 text-sm font-semibold uppercase tracking-[.14em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
                  >
                    Open Location
                  </a>
                </div>
              </div>

              <p className="mx-auto mt-8 max-w-3xl text-base leading-8 text-[#686861] sm:text-lg">
                Please use the wedding QR code to share your favourite moments from our
                special day.
              </p>
            </div>

            <div className="mx-auto mt-10 max-w-5xl">
              <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
	                <div className="overflow-hidden rounded-4xl border border-[#decf9b] bg-white shadow-sm">
	                  <div className="relative aspect-4/5 bg-[#ebe7df]">
                    <Image
                      src="/johnmax.jpg"
                      alt="John and Maxine"
                      fill
                      priority
                      className="object-cover"
                    />
                  </div>

                  <div className="border-t border-[#ead8a0] px-6 py-5 text-center">
                    <p
                      className={`${greatVibes.className} wedding-gold-halo text-3xl text-[#1f3f6d]`}
                    >
                      John &amp; Maxine
                    </p>
                    <p className="mt-2 text-sm uppercase tracking-[0.16em] text-[#8b8b84]">
                      Welcome to our wedding memories
                    </p>
                  </div>
                </div>

                <div className="flex h-full flex-col gap-6">
	                  <div className="rounded-4xl border border-[#decf9b] bg-[#fcfbf8] px-6 py-7 shadow-sm">
                    <p className="text-[11px] uppercase tracking-[0.28em] text-[#9a9a92]">
                      Share Your Memories
                    </p>

                    <h3 className="mt-3 text-4xl font-medium text-[#1f3f6d] sm:text-5xl">
                      Upload your photos and videos
                    </h3>

                    <p className="mt-4 text-base leading-8 text-[#686861]">
                      We would love for you to share the special moments you capture
                      throughout the day.
                    </p>

                    {uploadStatus ? (
	                      <div className="mt-4 rounded-2xl border border-[#ead8a0] bg-[#fffdfa] px-4 py-3 text-sm text-[#1f4e79]">
                        {uploadStatus}
                      </div>
                    ) : null}

                    <div className="mt-7 flex flex-col gap-4">
                      <label
                        htmlFor={uploadInputId}
                        role="button"
                        tabIndex={0}
                        onKeyDown={handleUploadKeyDown}
                        className="inline-flex min-h-14 cursor-pointer items-center justify-center rounded-full bg-[#1f4e79] px-8 py-3 text-sm font-semibold uppercase tracking-[.14em] text-white transition hover:bg-[#173d61]"
                      >
                        Upload Photos & Videos
                      </label>

                      <Link
                        href="/framed-photo"
                        className="inline-flex min-h-14 items-center justify-center rounded-full border border-[#caa23a]/60 bg-[#f8f5ed] px-8 py-3 text-sm font-semibold uppercase tracking-[.14em] text-[#1f4e79] transition hover:bg-[#f2ecdf]"
                      >
                        Take Framed Photo
                      </Link>

                      <Link
                        href="/guestbook"
                        className="inline-flex min-h-14 items-center justify-center rounded-full border-2 border-[#1f4e79]/30 bg-white px-8 py-3 text-sm font-semibold uppercase tracking-[.14em] text-[#1f4e79] shadow-[0_8px_18px_rgba(31,78,121,0.06)] transition hover:border-[#1f4e79]/45 hover:bg-[#f8f5ed]"
                      >
                        Sign the Guestbook
                      </Link>

                      <button
                        type="button"
                        onClick={toggleConfetti}
                        aria-pressed={!confettiEnabled}
                        className="self-center text-xs font-semibold uppercase tracking-[.14em] text-[#1f4e79] underline-offset-4 transition hover:underline"
                      >
                        {confettiEnabled ? "Turn Off Confetti" : "Turn On Confetti"}
                      </button>
                    </div>

                    <input
                      id={uploadInputId}
                      ref={uploadInputRef}
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      className="absolute h-px w-px overflow-hidden opacity-0"
                      onChange={handleFiles}
                    />
                  </div>

                  <div className="flex flex-1 flex-col rounded-[1.75rem] border border-[#decf9b] bg-white px-6 py-7 shadow-sm">
                    <p className="text-[11px] uppercase tracking-[0.28em] text-[#9a9a92]">
                      With Love
                    </p>

                    <p className="mt-4 text-base leading-8 text-[#686861]">
                      Thank you for being part of our celebration and for helping us
                      capture memories we can treasure forever.
                    </p>

                    <p className="mt-4 text-base leading-8 text-[#686861]">
                      Every photo, video, signature, and message you leave becomes part
                      of the story we will look back on for years to come.
                    </p>

                    <p className="mt-4 text-base leading-8 text-[#686861]">
                      We are so grateful to share this day with the people we love most,
                      and we hope you enjoy every moment of it with us.
                    </p>

                    <div className="mt-auto pt-6">
                      <p className={`${greatVibes.className} wedding-gold-halo text-3xl text-[#1f4e79]`}>
                        
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mx-auto mt-12 max-w-5xl rounded-[1.75rem] border border-[#decf9b] bg-[#fcfbf8] px-6 py-8 shadow-sm sm:px-8 sm:py-10">
              <div className="text-center">
                <p className="text-[11px] uppercase tracking-[0.34em] text-[#9a9a92]">
                  Browse Memories
                </p>

                <h3 className="mt-4 text-4xl font-medium text-[#1f3f6d] sm:text-5xl">
                  Shared moments from the day
                </h3>

                <div className="mt-4">
                  <GoldDivider />
                </div>
              </div>

              <div className="mt-8 grid gap-6 md:grid-cols-2">
                <Link
                  href="/photos"
                  className="overflow-hidden rounded-3xl border border-[#decf9b] bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="px-5 py-4">
                    <p
                      className={`${greatVibes.className} wedding-gold-halo text-4xl text-[#1f4e79]`}
                    >
                      Photos
                    </p>
                    <p className="mt-2 text-sm leading-7 text-[#686861]">
                      Browse the photo gallery from the day
                    </p>
                  </div>

                  <div className="px-5 pb-5">
                    <PreviewScroller items={photoItems} />
                  </div>
                </Link>

                <Link
                  href="/videos"
                  className="overflow-hidden rounded-3xl border border-[#decf9b] bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="px-5 py-4">
                    <p
                      className={`${greatVibes.className} wedding-gold-halo text-4xl text-[#1f4e79]`}
                    >
                      Videos
                    </p>
                    <p className="mt-2 text-sm leading-7 text-[#686861]">
                      Browse the video gallery from the day
                    </p>
                  </div>

                  <div className="px-5 pb-5">
                    <PreviewScroller items={videoItems.length ? videoItems : [previewItems[2]]} />
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
